import {
  Box,
  Center,
  Button,
  Text,
  Paper,
  Group,
  Modal,
  TextInput,
  Image as MantineImage,
  Menu,
  Stack,
} from '@mantine/core';
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { deleteNoBody, getJson, isApiError } from '@/services/httpClient';
import { SessionAuth } from 'supertokens-auth-react/recipe/session';
import Session from 'supertokens-auth-react/recipe/session';
import { loadAuthenticatedUserProfile } from '../services/Authentication';
import { useStore, UserProfile, type UserPermissions, initialPermissions } from '../store/Store';
import {
  userPath,
  websiteDomain,
  clientRoutes,
  layoutPath,
} from '../ApiPaths';
import { pageWrapper } from '@/Styles';
import { copyToClipboard } from '@/services/Utility';
import { useDeviceDetection } from '@/hooks/useDeviceDetection';
import { ListingStatusBadge, ArticleNumberBadge, ProductCardOverlayChip, VisibilityBadge } from '../components/badges';
// Temporarily hidden — categories UI disabled
// import { PRODUCT_CATEGORIES } from '@/constants/productCategories';
import { EditorChipButton } from '@/components/EditorChipButton';
import { ProductListPreviewPicture } from '../components/ProductListPreviewPicture';
import {
  IconEdit,
  IconTrash,
  IconShare,
  IconCode,
  IconDeviceDesktopAnalytics,
  Icon3dCubeSphere,
  IconEye,
  IconSearch,
  IconPhoto,
  IconArrowsSort,
  IconSortAscending,
  IconSortDescending,
  IconSelector,
  IconCheck,
  IconRotateClockwise,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
  getListingsByProductId,
  getProjectsByUser,
  deleteProject,
  getProductsByUser,
  deleteProduct as deleteProductAPI,
  getDeletedProducts,
  restoreDeletedProduct,
  permanentDeleteDeletedProduct,
} from '@/Api';
import { IPopulatedListing } from '@/types/Listing.model';
import { IProject } from '@/types/Project.model';
import { IProduct } from '@/types/Product.model';
import * as modelViewer from '@google/model-viewer';
import React from 'react';
import { ProductCard } from '@/components/ProductCard';
import { CreateProductCard } from '@/components/CreateProductCard';
import { ProjectList } from '@/components/projects';
import { PageHeader } from '@/components/PageHeader';
import { DashboardCollapsibleSection } from '@/components/DashboardCollapsibleSection/DashboardCollapsibleSection';
import { useTranslation } from 'react-i18next';
import { Annotation, ModelViewerSettings } from '@/types/Annotation.model';
import { getProductById, isProductLockedResponse } from '@/Api';
import { ProductShareModal, type ProductShareTab } from '@/components/ProductShareModal/ProductShareModal';
import { ProductQRCodeDialog } from '@/components/ProductShare';
import type { LinkShareMode } from '@/types/Product.model';
import {
  AnnotationNavigator,
  AnnotationNavigatorRef,
  AnnotationImageModal,
  AnnotationProductModal,
  AnnotationHotspot,
  AnnotationInfoBox,
  useAnnotationBarPortalTargetStyle,
} from '@/components/Annotations';
import { AnimationSelector } from '@/components/AnimationSelector/AnimationSelector';
import {
  DEFAULT_MODEL_VIEWER_PRESENTATION,
  presentationSettingsToAttrs,
  presentationSettingsToViewerStyle,
  resolvePresentationSettings,
  type ModelViewerPresentationSettings,
} from '@/constants/modelViewerPresentation';
import { MODEL_VIEWER_RELAXED_ZOOM_ATTRS } from '@/utils/annotationConstants';
import { useAnnotationClickOutside } from '@/hooks/useAnnotationClickOutside';
import { useAnnotationClick } from '@/hooks/useAnnotationClick';
import { useAnnotationPositionUpdate } from '@/hooks/useAnnotationPositionUpdate';
import { useARButtonVisibility } from '@/hooks/useARButtonVisibility';
import { useModelViewerDynamicZoom } from '@/hooks/useModelViewerDynamicZoom';
import { glbZoomSrc, productGlbUrl, productUsdzUrl } from '@/utils/productModelUrl';
import { ModelViewerResetZoomButton } from '@/components/ModelViewer/ModelViewerResetZoomButton';
import { ModelViewerScreenshotButton } from '@/components/ModelViewer/ModelViewerScreenshotButton';
import { ModelViewerPresentationPanel } from '@/components/ModelViewer/ModelViewerPresentationPanel';
import { ViewerArButton, ViewerPlayPauseButton } from '@/components/ModelViewer/ViewerControlButton';
import { MarketplaceFiltersDropdown } from '@/components/MarketplaceFiltersDropdown';
import { MarketplaceToolbarButton } from '@/components/MarketplaceToolbarButton';
import { VirtualizedProductCardGrid } from '@/components/VirtualizedProductCardGrid';
import { DashboardInsights } from '@/components/DashboardInsights/DashboardInsights';
import type { ProductStarState } from '@/hooks/useProductStarToggle';
import dashboardModalClasses from './DashboardProductModal.module.css';
import { tagsStringToArray } from '@/utils/tags';
import { isMobile } from 'is-mobile';

interface ProductWithListings extends Partial<Omit<IProduct, 'owningUser_id'>> {
  listings?: IPopulatedListing[];
  owningUser_id?: Partial<UserProfile>;
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  width: '80%',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px',
  borderRadius: '10px',
  backgroundColor: 'color-mix(in srgb, var(--mantine-color-default) 65%, transparent)',
};


function ShowLinks({
  products,
  productSlots,
  deleteProduct,
  corporateColor,
  isLayout,
  onProductsRefresh,
  onStarChange,
}: {
  products: ProductWithListings[];
  productSlots: number;
  deleteProduct: (id: string) => void;
  corporateColor: string;
  isLayout: boolean;
  /** Refresh product list after inline edits (e.g. link share modal save). */
  onProductsRefresh?: () => void;
  onStarChange?: (productId: string, state: ProductStarState) => void;
}) {
  const { t } = useTranslation();
  const [format, setFormat] = useState<string>('Querformat');
  const [selectedProduct, setSelectedProduct] = useState<ProductWithListings | null>(null);
  /** Opens ProductShareModal from the product detail modal (not from the card). */
  const [productShareModalOpened, setProductShareModalOpened] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const modelViewerRef = useRef<modelViewer.ModelViewerElement>(null);
  const annotationNavigatorRef = useRef<AnnotationNavigatorRef>(null);

  // Gate on showModal so the zoom hook re-binds when the modal remounts model-viewer.
  const dashboardModalGlbSrc = glbZoomSrc(
    selectedProduct,
    selectedProduct?._id,
    showModal
  );
  useModelViewerDynamicZoom(modelViewerRef, dashboardModalGlbSrc);
  const annotationBarPortalStyle = useAnnotationBarPortalTargetStyle();

  const [isPlaying, setIsPlaying] = useState(true);
  const [showARInfo, setShowARInfo] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [shareInitialTab, setShareInitialTab] = useState<ProductShareTab>('link');
  const deviceInfo = useDeviceDetection();
  const isDesktop = deviceInfo.isDesktop;
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [annotationsVisible, setAnnotationsVisible] = useState<boolean>(true);
  const [activeAnnotation, setActiveAnnotation] = useState<Annotation | null>(null);
  const [annotationPosition, setAnnotationPosition] = useState<{ x: number; y: number } | null>(null);
  const [initialCameraOrbit, setInitialCameraOrbit] = useState<string>('');
  const [initialCameraTarget, setInitialCameraTarget] = useState<string>('');
  const [initialCameraFieldOfView, setInitialCameraFieldOfView] = useState<string>('');
  const [presentationSettings, setPresentationSettings] =
    useState<ModelViewerPresentationSettings>(DEFAULT_MODEL_VIEWER_PRESENTATION);
  const presentationAttrs = useMemo(
    () => presentationSettingsToAttrs(presentationSettings),
    [presentationSettings]
  );
  const presentationViewerStyle = useMemo(
    () => presentationSettingsToViewerStyle(presentationSettings),
    [presentationSettings]
  );
  const [imageModalOpened, setImageModalOpened] = useState(false);
  const [productModalOpened, setProductModalOpened] = useState(false);
  const [selectedAnnotationProduct, setSelectedAnnotationProduct] = useState<IProduct | null>(null);
  const descriptionBoxRef = useRef<HTMLDivElement>(null);
  const [hasAnimations, setHasAnimations] = useState<boolean>(false);

  // Close description box when clicking outside
  useAnnotationClickOutside({
    descriptionBoxRef,
    activeAnnotation,
    onClose: () => {
      setActiveAnnotation(null);
      setAnnotationPosition(null);
      setImageModalOpened(false);
      setProductModalOpened(false);
      setSelectedAnnotationProduct(null);
    },
    imageModalOpened,
    productModalOpened,
  });

  // Show/hide native AR button when model-viewer loads
  useARButtonVisibility({ modelViewerRef, isDesktop });

  // Check if model has animations
  useEffect(() => {
    const modelViewer = modelViewerRef.current;
    if (!modelViewer) return;

    const checkAnimations = () => {
      try {
        const animations = (modelViewer as any).availableAnimations || [];
        const validAnimations = animations.filter((name: string) => name && name.trim() !== '');
        setHasAnimations(validAnimations.length > 0);
      } catch (error) {
        console.error('Error checking animations:', error);
        setHasAnimations(false);
      }
    };

    const handleLoad = () => {
      setTimeout(checkAnimations, 100);
    };

    modelViewer.addEventListener('load', handleLoad);
    
    // Also check periodically in case animations load after the load event
    const interval = setInterval(() => {
      if (modelViewerRef.current) {
        checkAnimations();
      }
    }, 500);

    // Initial check
    checkAnimations();

    return () => {
      modelViewer.removeEventListener('load', handleLoad);
      clearInterval(interval);
    };
  }, [modelViewerRef]);

  const openShareModal = (product: ProductWithListings, tab: ProductShareTab = 'link') => {
    setSelectedProduct(product);
    setShareInitialTab(tab);
    setProductShareModalOpened(true);
  };

  const openProductModal = async (product: ProductWithListings) => {
    setSelectedProduct(product);
    setShowModal(true);
    setActiveAnnotation(null);
    
    if (!product._id) return;
    
    try {
      const fullProduct = await getProductById(product._id);
      if (isProductLockedResponse(fullProduct)) {
        setAnnotations([]);
        return;
      }
      const settings: ModelViewerSettings = fullProduct.modelViewerSettings 
        ? JSON.parse(fullProduct.modelViewerSettings) 
        : {};
      
      if (settings.initialCameraOrbit) setInitialCameraOrbit(settings.initialCameraOrbit);
      else setInitialCameraOrbit('');
      if (settings.initialCameraTarget) setInitialCameraTarget(settings.initialCameraTarget);
      else setInitialCameraTarget('');
      if (settings.initialCameraFieldOfView) {
        setInitialCameraFieldOfView(settings.initialCameraFieldOfView);
      } else setInitialCameraFieldOfView('');
      setAnnotations(Array.isArray(settings.annotations) ? settings.annotations : []);
      setPresentationSettings(resolvePresentationSettings(settings));
    } catch (error) {
      console.error('Error loading product:', error);
      setAnnotations([]);
    }
  };

  const toggleAnimation = () => {
    if (isPlaying) {
      modelViewerRef.current?.pause();
    } else {
      modelViewerRef.current?.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleARClick = () => {
    // Desktop: Show QR code (no AR support)
    // Mobile/Tablet/VR: Use native AR (model-viewer's built-in AR button)
    if (deviceInfo.isDesktop) {
      setShowARInfo(false);
      setShowQRCode(true);
    }
  };

  const handleAnnotationClick = useAnnotationClick({
    modelViewerRef,
    annotationNavigatorRef,
    setActiveAnnotation,
    setAnnotationPosition,
    onCloseModals: () => {
      setImageModalOpened(false);
      setProductModalOpened(false);
      setSelectedAnnotationProduct(null);
    },
  });

  // Update annotation position when camera moves
  useAnnotationPositionUpdate({
    modelViewerRef,
    activeAnnotation,
    setAnnotationPosition,
  });

  return (
    <div style={{ width: '100%' }}>
      <VirtualizedProductCardGrid
        items={products}
        isMobileLayout={isMobile()}
        leadingSlot={
          products.length < productSlots && !isLayout ? (
            <div style={{ width: '230px', margin: '0 0 15px 0' }}>
              <CreateProductCard href={clientRoutes.editproduct} corporateColor={corporateColor} />
            </div>
          ) : undefined
        }
        renderItem={(product) => (
          <div key={product._id} style={{ width: '230px', margin: '0 0 15px 0' }}>
            <ProductCard
              product={product}
              isLayout={isLayout}
              onClick={() => {
                if (isLayout) {
                  window.location.href = `${websiteDomain}${clientRoutes.layout}`.replace(
                    ':id',
                    product._id as string
                  );
                } else {
                  openProductModal(product);
                }
              }}
              showActions={true}
              showDeleteOnly={isLayout}
              corporateColor={corporateColor}
              showArticleNumber={true}
              showListingStatus={true}
              showPrice={true}
              showVisibility={true}
              onStarChange={onStarChange}
              onShareLink={async () => {
                if (!product._id) return;
                const id = product._id as string;
                if (isLayout) {
                  const url = `${websiteDomain}${clientRoutes.layout}`.replace(':id', id);
                  try {
                    await copyToClipboard(url);
                    notifications.show({
                      title: t('dashboard.notifications.success'),
                      message: t('dashboard.notifications.linkCopied'),
                    });
                  } catch {
                    notifications.show({
                      title: t('dashboard.notifications.error'),
                      message: t('dashboard.notifications.copyFailed'),
                      color: 'red',
                    });
                  }
                  return;
                }
                openShareModal(product, 'link');
              }}
              onEdit={() =>
                (window.location.href = clientRoutes.editproduct + '?id=' + product._id)
              }
              onDelete={() => deleteProduct(product._id as string)}
            />
          </div>
        )}
      />

      <Modal
        opened={showModal}
        onClose={() => {
          setShowModal(false);
          setProductShareModalOpened(false);
        }}
        title={selectedProduct?.productName || ''}
        size="xl"
        styles={{
          content: {
            padding: 0,
            overflow: 'hidden',
          },
          header: {
            position: 'absolute',
            top: 0,
            right: 0,
            left: 'auto',
            width: 'auto',
            padding: '10px 12px',
            margin: 0,
            minHeight: 'unset',
            background: 'transparent',
            borderBottom: 'none',
            zIndex: 30,
          },
          title: {
            display: 'none',
          },
          body: {
            padding: 0,
          },
        }}
        centered
        radius="md"
        shadow="xl"
        fullScreen={window.innerWidth < 768}
      >
        <Paper radius={0} shadow="none" className={dashboardModalClasses.modalHeader}>
          <div className={dashboardModalClasses.modalHeaderTop}>
            <h2 className={dashboardModalClasses.modalTitle}>{selectedProduct?.productName}</h2>
            <ProductCardOverlayChip
              variant="views"
              icon={<IconEye size={9} stroke={2} />}
              className={dashboardModalClasses.modalViewsChip}
            >
              {selectedProduct?.views || 0} {t('dashboard.metrics.clicks')}
            </ProductCardOverlayChip>
          </div>

          {!isLayout && (
            <div className={dashboardModalClasses.modalMetaRow}>
              <VisibilityBadge
                isPublic={selectedProduct?.isPublic}
                linkShareMode={selectedProduct?.linkShareMode}
                appearance="card"
              />
              {selectedProduct?.articleNumber && (
                <ArticleNumberBadge
                  articleNumber={selectedProduct.articleNumber}
                  appearance="card"
                />
              )}
              {selectedProduct?.listings && (
                <ListingStatusBadge listings={selectedProduct.listings} appearance="card" />
              )}
            </div>
          )}
        </Paper>

        <div className={dashboardModalClasses.modalBody}>
          {selectedProduct &&
            selectedProduct._id &&
            selectedProduct.availableFormats?.includes('.glb') && (
              <Box style={{ position: 'relative' }}>
                <ModelViewerResetZoomButton
                  modelViewerRef={modelViewerRef}
                  cameraDefaults={{
                    cameraOrbit: initialCameraOrbit,
                    cameraTarget: initialCameraTarget,
                    cameraFieldOfView: initialCameraFieldOfView,
                  }}
                />
                <ModelViewerScreenshotButton
                  modelViewerRef={modelViewerRef}
                  fileName={selectedProduct.productName}
                />
                <model-viewer
                  key={selectedProduct._id}
                  ref={modelViewerRef}
                  style={{
                    width: '100%',
                    height: '500px',
                    overflow: 'hidden',
                    ...presentationViewerStyle,
                  }}
                  src={productGlbUrl(selectedProduct._id)}
                  ios-src={
                    selectedProduct.availableFormats.includes('.usdz')
                      ? productUsdzUrl(selectedProduct._id)
                      : ''
                  }
                  ar
                  ar-modes="webxr scene-viewer quick-look"
                  ar-scale="auto"
                  ar-placement="floor"
                  interaction-prompt="auto"
                  interaction-prompt-style="basic"
                  controls="play pause"
                  {...presentationAttrs}
                  camera-controls
                  {...MODEL_VIEWER_RELAXED_ZOOM_ATTRS}
                  touch-action="pan-y"
                  enable-pan
                  autoPlay
                  {...(initialCameraOrbit && { 'camera-orbit': initialCameraOrbit })}
                  {...(initialCameraTarget && { 'camera-target': initialCameraTarget })}
                  {...(initialCameraFieldOfView && { 'field-of-view': initialCameraFieldOfView })}
                >
                  {annotationsVisible && annotations.map((annotation) => (
                    <AnnotationHotspot
                      key={annotation.id}
                      annotation={annotation}
                      onClick={handleAnnotationClick}
                    />
                  ))}
                </model-viewer>
                
                {/* Portal target for annotation bar — left on mobile, centered on desktop */}
                <Box id="dashboard-annotation-bar" style={annotationBarPortalStyle} />
                
                {/* 3-dot menu + movie + play/pause + AR - bottom right */}
                <Box
                  style={{
                    position: 'absolute',
                    bottom: '16px',
                    right: '16px',
                    zIndex: 12,
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                  }}
                  className="desktop-ar-buttons"
                >
                  <ModelViewerPresentationPanel
                    settings={presentationSettings}
                    onChange={setPresentationSettings}
                    modelViewerRef={modelViewerRef}
                    color={corporateColor}
                    inline
                  />
                  <AnnotationNavigator
                    ref={annotationNavigatorRef}
                    annotations={annotations}
                    modelViewerRef={modelViewerRef}
                    color={corporateColor}
                    onAnnotationChange={setActiveAnnotation}
                    onAnnotationsVisibleChange={setAnnotationsVisible}
                    hasAnimationSelector={hasAnimations}
                    inline={true}
                    barPortalTargetId="dashboard-annotation-bar"
                  />
                  <Box style={{ position: 'relative', zIndex: 12 }}>
                    <AnimationSelector
                      modelViewerRef={modelViewerRef}
                      color={corporateColor}
                    />
                  </Box>
                  <ViewerPlayPauseButton
                    accentColor={corporateColor}
                    isPlaying={isPlaying}
                    onClick={toggleAnimation}
                  />
                  {isDesktop ? (
                    <ViewerArButton accentColor={corporateColor} onClick={handleARClick} />
                  ) : null}
                </Box>

                {/* Active Annotation Info Box - Positioned next to clicked pivot */}
                {activeAnnotation && annotationPosition && (
                  <AnnotationInfoBox
                    activeAnnotation={activeAnnotation}
                    annotationPosition={annotationPosition}
                    descriptionBoxRef={descriptionBoxRef}
                    backgroundColor={corporateColor}
                    onProductClick={(product) => {
                      setSelectedAnnotationProduct(product);
                      setProductModalOpened(true);
                    }}
                    onImageClick={() => setImageModalOpened(true)}
                  />
                )}

                {/* Image Modal */}
                <AnnotationImageModal
                  opened={imageModalOpened}
                  onClose={() => setImageModalOpened(false)}
                  annotation={activeAnnotation}
                  ImageComponent={MantineImage}
                />

                {/* Product 3D Modal */}
                <AnnotationProductModal
                  opened={productModalOpened}
                  onClose={() => {
                    setProductModalOpened(false);
                    setSelectedAnnotationProduct(null);
                  }}
                  product={selectedAnnotationProduct}
                />
              </Box>
            )}

          <Box px="md" py="lg" className={dashboardModalClasses.modalFooter}>
            <Group justify="flex-end" gap="sm" wrap="wrap">
              <EditorChipButton
                variant="primary"
                accentColor={corporateColor}
                leftIcon={<Icon3dCubeSphere size={15} stroke={1.75} />}
                onClick={() => {
                  if (selectedProduct?._id) {
                    window.location.href = isLayout
                      ? `${websiteDomain}${clientRoutes.layout}`.replace(
                          ':id',
                          selectedProduct._id as string
                        )
                      : `${websiteDomain}${clientRoutes.product}`.replace(
                          ':id',
                          selectedProduct._id as string
                        );
                  }
                }}
              >
                {isLayout
                  ? t('dashboard.actions.viewLayout')
                  : t('dashboard.actions.viewProduct')}
              </EditorChipButton>

              <EditorChipButton
                accentColor={corporateColor}
                leftIcon={<IconShare size={15} stroke={1.75} />}
                onClick={async () => {
                  if (!selectedProduct) return;
                  openShareModal(selectedProduct, 'link');
                }}
              >
                {t('dashboard.actions.shareLink')}
              </EditorChipButton>

              {!isLayout && selectedProduct?._id && (
                <EditorChipButton
                  accentColor={corporateColor}
                  leftIcon={<IconEdit size={15} stroke={1.75} />}
                  onClick={() => {
                    window.location.href =
                      clientRoutes.editproduct + '?id=' + selectedProduct._id;
                  }}
                >
                  {t('dashboard.actions.edit')}
                </EditorChipButton>
              )}

              <EditorChipButton
                variant="danger"
                leftIcon={<IconTrash size={15} stroke={1.75} />}
                onClick={() => {
                  if (selectedProduct?._id) {
                    deleteProduct(selectedProduct._id);
                    setShowModal(false);
                  }
                }}
              >
                {t('dashboard.actions.delete')}
              </EditorChipButton>
            </Group>
          </Box>
        </div>
      </Modal>

      <ProductQRCodeDialog
        opened={showQRCode}
        onClose={() => {
          setShowQRCode(false);
          setShowARInfo(false);
        }}
        url={
          selectedProduct?._id
            ? `${websiteDomain}${clientRoutes.product.replace(':id', selectedProduct._id as string)}`
            : ''
        }
        accentColor={corporateColor}
        showArInfo={showARInfo}
      />

      <ProductShareModal
        opened={productShareModalOpened && Boolean(selectedProduct?._id) && !isLayout}
        onClose={() => setProductShareModalOpened(false)}
        productId={(selectedProduct?._id as string) ?? ''}
        productName={selectedProduct?.productName}
        linkShareMode={(selectedProduct?.linkShareMode as LinkShareMode) ?? 'open'}
        isOwner
        initialTab={shareInitialTab}
        accentColor={corporateColor}
        onLinkSaved={(patch) => {
          setSelectedProduct((p) =>
            p ? ({ ...p, ...patch } as ProductWithListings) : null
          );
          onProductsRefresh?.();
        }}
      />
    </div>
  );
}

/** Get tags for a single product from tags array or metadata.rows[id].data.Tags */
function getProductTags(p: ProductWithListings): string[] {
  if (p.tags?.length) return p.tags;
  const tagsStr = p.metadata?.rows?.[p._id as string]?.data?.Tags;
  if (typeof tagsStr === 'string') return tagsStringToArray(tagsStr);
  return [];
}

/** Same retention window as server soft-delete purge (7 days). */
const TRASH_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

function daysRemainingInTrash(deletedAt: string | Date | null | undefined): number {
  if (!deletedAt) return 0;
  const end = new Date(deletedAt).getTime() + TRASH_RETENTION_MS;
  return Math.max(0, Math.ceil((end - Date.now()) / (24 * 60 * 60 * 1000)));
}

export function DashBoard() {
  const { t } = useTranslation();
  const [products, setProducts] = useState<ProductWithListings[]>([]);
  const [layouts, setLayouts] = useState<ProductWithListings[]>([]);
  const [projects, setProjects] = useState<IProject[]>([]);
  const [productSearchValue, setProductSearchValue] = useState('');
  const [layoutSearchValue, setLayoutSearchValue] = useState('');
  const [filteredProducts, setFilteredProducts] = useState<ProductWithListings[] | null>(null);
  const [filteredLayouts, setFilteredLayouts] = useState<ProductWithListings[] | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  // Temporarily hidden — categories UI disabled
  // const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'public' | 'private'>('all');
  const [sortOption, setSortOption] = useState<'none' | 'date-asc' | 'views-desc' | 'views-asc'>('none');
  const [deletedProducts, setDeletedProducts] = useState<IProduct[]>([]);
  const userProfile = useStore((state) => state.userProfile);
  const userPermissions = useStore((state) => state.userPermissions) ?? initialPermissions;

  // Tags and counts derived from user's products (for filter dropdown)
  const { allTagsList, tagCounts } = useMemo(() => {
    const countMap: Record<string, number> = {};
    const tagSet = new Set<string>();
    products.forEach((p) => {
      const tags = getProductTags(p);
      tags.forEach((tag) => {
        tagSet.add(tag);
        countMap[tag] = (countMap[tag] ?? 0) + 1;
      });
    });
    return {
      allTagsList: Array.from(tagSet).sort((a, b) => a.localeCompare(b)),
      tagCounts: countMap,
    };
  }, [products]);

  /* Temporarily hidden — categories UI disabled
  const { allCategoriesList, categoryCounts } = useMemo(() => {
    const countMap: Record<string, number> = {};
    const catSet = new Set<string>(PRODUCT_CATEGORIES);
    products.forEach((p) => {
      const cat = typeof p.category === 'string' ? p.category.trim() : '';
      if (!cat) return;
      catSet.add(cat);
      countMap[cat] = (countMap[cat] ?? 0) + 1;
    });
    return {
      allCategoriesList: Array.from(catSet).sort((a, b) => a.localeCompare(b)),
      categoryCounts: countMap,
    };
  }, [products]);
  */

  const loadDeletedProducts = async () => {
    const sessionUserId = await Session.getUserId();
    if (!sessionUserId) return;
    try {
      const list = await getDeletedProducts(sessionUserId);
      setDeletedProducts(list);
    } catch (error) {
      console.error(error);
    }
  };

  const getProducts = async () => {
    const sessionUserId = await Session.getUserId();
    if (!sessionUserId) return;
    if (userProfile._id !== sessionUserId) {
      await loadAuthenticatedUserProfile(sessionUserId);
    }
    try {
      const products = await getProductsByUser(sessionUserId);

      // Fetch listings for each product
      const productsWithListings = await Promise.all(
        products.map(
          async (product: IProduct): Promise<ProductWithListings> => {
            if (!product._id) {
              return { ...product, listings: undefined } as ProductWithListings;
            }
            try {
              const listings = await getListingsByProductId(product._id);
              return { ...product, listings } as ProductWithListings;
            } catch {
              return { ...product, listings: undefined } as ProductWithListings;
            }
          }
        )
      );

      setProducts(productsWithListings);

      const permissionData = await getJson<UserPermissions>(
        `${userPath}${sessionUserId}/permissions`
      );
      useStore.getState().setUserPermissions(
        permissionData ?? { ...initialPermissions, _id: sessionUserId }
      );
      await loadDeletedProducts();
    } catch (error) {
      console.error(error);
    }
  };

  const getLayouts = async () => {
    const sessionUserId = await Session.getUserId();
    if (!sessionUserId) return;
    getJson<ProductWithListings[]>(`${layoutPath}byUser/${sessionUserId}`)
      .then((layoutData) => {
        setLayouts(layoutData);
        return getJson<UserPermissions>(`${userPath}${sessionUserId}/permissions`);
      })
      .then((permissionData) =>
        useStore.getState().setUserPermissions(
          (permissionData as UserPermissions | null) ?? {
            ...initialPermissions,
            _id: sessionUserId,
          }
        )
      )
      .catch((error) => console.error(error));
  };

  const getProjects = async () => {
    const sessionUserId = await Session.getUserId();
    if (!sessionUserId) return;
    try {
      const projects = await getProjectsByUser(sessionUserId);
      setProjects(projects);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const deleteProduct = async (id: string) => {
    if (!window.confirm(t('dashboard.actions.deleteConfirm'))) return;
    try {
      await deleteProductAPI(id);
      notifications.show({
        title: t('dashboard.notifications.success'),
        message: t('dashboard.notifications.productMovedToTrash'),
        color: 'green',
      });
      getProducts();
    } catch (error) {
      console.error(error);
      const msg =
        isApiError(error) && error.response?.data && typeof error.response.data === 'object'
          ? (error.response.data as { message?: string }).message
          : undefined;
      notifications.show({
        title: t('dashboard.notifications.error'),
        message: msg || t('dashboard.notifications.error'),
        color: 'red',
      });
    }
  };

  const handleRestoreDeletedProduct = async (productId: string) => {
    try {
      await restoreDeletedProduct(productId);
      notifications.show({
        title: t('dashboard.notifications.success'),
        message: t('dashboard.notifications.restoreSuccess'),
        color: 'green',
      });
      getProducts();
    } catch (error) {
      console.error(error);
      const msg =
        isApiError(error) && error.response?.data && typeof error.response.data === 'object'
          ? (error.response.data as { message?: string }).message
          : undefined;
      notifications.show({
        title: t('dashboard.notifications.error'),
        message: msg || t('dashboard.notifications.restoreError'),
        color: 'red',
      });
    }
  };

  const handlePermanentDeleteFromTrash = async (productId: string) => {
    if (!window.confirm(t('dashboard.trash.permanentConfirm'))) return;
    try {
      await permanentDeleteDeletedProduct(productId);
      notifications.show({
        title: t('dashboard.notifications.success'),
        message: t('dashboard.notifications.permanentDeleteSuccess'),
        color: 'green',
      });
      getProducts();
    } catch (error) {
      console.error(error);
      const msg =
        isApiError(error) && error.response?.data && typeof error.response.data === 'object'
          ? (error.response.data as { message?: string }).message
          : undefined;
      notifications.show({
        title: t('dashboard.notifications.error'),
        message: msg || t('dashboard.notifications.permanentDeleteError'),
        color: 'red',
      });
    }
  };

  const deleteLayout = async (id: string) => {
    try {
      await deleteNoBody(`${layoutPath}${id}`);
      notifications.show({
        title: t('dashboard.notifications.success'),
        message: t('dashboard.notifications.layoutDeleted'),
        color: 'green',
      });
      getLayouts();
    } catch (error) {
      console.error(error);
      const msg =
        isApiError(error) && error.response?.data && typeof error.response.data === 'object'
          ? (error.response.data as { message?: string }).message
          : undefined;
      notifications.show({
        title: t('dashboard.notifications.error'),
        message: msg || t('dashboard.notifications.layoutDeleteError'),
        color: 'red',
      });
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    try {
      await deleteProject(projectId);
      getProjects(); // Refresh the projects list
      notifications.show({
        title: t('dashboard.success'),
        message: t('dashboard.projectDeleted'),
        color: 'green',
      });
    } catch (error) {
      console.error('Error deleting project:', error);
      notifications.show({
        title: t('dashboard.error'),
        message: t('dashboard.deleteProjectError'),
        color: 'red',
      });
    }
  };

  const handleLeaveProject = (projectId: string) => {
    setProjects((prevProjects) => prevProjects.filter((project) => project._id !== projectId));
  };

  const handleUpdateProject = (updatedProject: IProject) => {
    setProjects((prevProjects) =>
      prevProjects.map((project) => (project._id === updatedProject._id ? updatedProject : project))
    );
  };

  useEffect(() => {
    void Session.getUserId().then((id) => {
      if (!id) return;
      if (userProfile._id !== id) {
        void loadAuthenticatedUserProfile(id);
      }
    });
  }, [userProfile._id]);

  useEffect(() => {
    void Session.doesSessionExist().then((hasSession) => {
      if (!hasSession) return;
      getProducts();
      getLayouts();
      getProjects();
    });
  }, [userProfile._id]);

  useEffect(() => {
    if (!productSearchValue) {
      setFilteredProducts(null);
      return;
    }
    const timeoutId = window.setTimeout(() => {
      const q = productSearchValue.toLowerCase();
      setFilteredProducts(
        products.filter((product) => JSON.stringify(product).toLowerCase().includes(q))
      );
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [productSearchValue, products]);

  // Sort products by option (same as marketplace)
  const sortProducts = (list: ProductWithListings[]) => {
    if (sortOption === 'none') return [...list];
    if (sortOption === 'date-asc') return [...list].reverse(); // Oldest first (API order is newest first)
    return [...list].sort((a, b) => {
      const viewsA = a.views ?? 0;
      const viewsB = b.views ?? 0;
      if (sortOption === 'views-desc') return viewsB - viewsA;
      if (sortOption === 'views-asc') return viewsA - viewsB;
      return 0;
    });
  };

  // Products list: search → tag filter → /* category → */ visibility → sort
  const productsAfterSearch = filteredProducts ?? products;
  const productsAfterTags =
    selectedTags.length === 0
      ? productsAfterSearch
      : productsAfterSearch.filter((p) => {
          const pt = getProductTags(p);
          return selectedTags.some((tag) => pt.includes(tag));
        });
  /* Temporarily hidden — categories UI disabled
  const productsAfterCategories =
    selectedCategories.length === 0
      ? productsAfterTags
      : productsAfterTags.filter(
          (p) => p.category && selectedCategories.includes(p.category)
        );
  */
  const productsAfterVisibility =
    visibilityFilter === 'all'
      ? productsAfterTags // was: productsAfterCategories
      : productsAfterTags.filter((p) =>
          visibilityFilter === 'public' ? p.isPublic === true : p.isPublic === false
        );
  const productsToShow = sortProducts(productsAfterVisibility);

  const patchProductStar = useCallback((productId: string, state: ProductStarState) => {
    const patch = (list: ProductWithListings[]) =>
      list.map((product) => (product._id === productId ? { ...product, ...state } : product));
    setProducts(patch);
    setFilteredProducts((prev) => (prev ? patch(prev) : null));
  }, []);

  useEffect(() => {
    if (!layoutSearchValue) {
      setFilteredLayouts(null);
      return;
    }
    setFilteredLayouts(
      layouts.filter((layout) =>
        JSON.stringify(layout).toLowerCase().includes(layoutSearchValue.toLowerCase())
      )
    );
  }, [layoutSearchValue, layouts]);

  // Deep link from notification center: /dashboard#trash
  useEffect(() => {
    if (window.location.hash !== '#trash') return;
    const timer = window.setTimeout(() => {
      document.getElementById('trash')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
    return () => window.clearTimeout(timer);
  }, [deletedProducts.length]);

  return (
    <SessionAuth>
      <Center style={pageWrapper}>
        <Box style={{ width: '80%' }}>
          <Box style={{ position: 'sticky', top: 0, zIndex: 1 }}>
            <Box mb={30} mt={20}>
              <PageHeader
                icon={<IconDeviceDesktopAnalytics size={22} stroke={1.75} />}
                title={t('dashboard.header.title')}
                subtitle={t('dashboard.header.subtitle')}
                accentColor={userProfile.qrCodeColor}
                mb={20}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '15px',
                    marginBottom: '10px',
                  }}
                >
                  <Icon3dCubeSphere
                    size={20}
                    style={{ color: userProfile.qrCodeColor, marginTop: '2px' }}
                  />
                  <Text size="sm" color="dimmed">
                    {t('dashboard.info.manageProducts')}
                  </Text>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
                  <IconCode
                    size={20}
                    style={{ color: userProfile.qrCodeColor, marginTop: '2px' }}
                  />
                  <Text size="sm" color="dimmed">
                    {t('dashboard.info.shareProducts')}
                  </Text>
                </div>
              </PageHeader>
            </Box>

            <DashboardInsights
              products={products}
              accentColor={userProfile.qrCodeColor}
              userId={userProfile._id}
            />

            <DashboardCollapsibleSection
              title={t('dashboard.counts.projectsCount', { count: projects.length })}
              accentColor={userProfile.qrCodeColor}
              expandLabel={t('dashboard.sections.expandProjects')}
              collapseLabel={t('dashboard.sections.collapseProjects')}
              mb={15}
              mt={10}
            >
              <ProjectList
                projects={projects}
                corporateColor={userProfile.qrCodeColor}
                onDeleteProject={handleDeleteProject}
                onLeaveProject={handleLeaveProject}
                onUpdateProject={handleUpdateProject}
                currentUserId={userProfile._id}
              />
            </DashboardCollapsibleSection>

            <DashboardCollapsibleSection
              title={t('dashboard.counts.productsCount', {
                count: products.length || 0,
                total: userPermissions.productSlots || 0,
              })}
              accentColor={userProfile.qrCodeColor}
              expandLabel={t('dashboard.sections.expandProducts')}
              collapseLabel={t('dashboard.sections.collapseProducts')}
              mb={15}
              mt={30}
            >
              <Group mb={10} align="flex-start" justify="space-between" wrap="wrap" gap="xs">
                <TextInput
                  placeholder={t('dashboard.search.products')}
                  value={productSearchValue}
                  onChange={(e) => setProductSearchValue(e.currentTarget.value)}
                  leftSection={<IconSearch size={16} />}
                  size="sm"
                  style={{ flex: 1, minWidth: 200 }}
                />
                <Group gap="xs" wrap="wrap">
                  <MarketplaceFiltersDropdown
                    tags={allTagsList}
                    tagCounts={tagCounts}
                    selectedTags={selectedTags}
                    onTagsChange={setSelectedTags}
                    // Temporarily hidden — categories UI disabled
                    // categories={allCategoriesList}
                    // categoryCounts={categoryCounts}
                    // selectedCategories={selectedCategories}
                    // onCategoriesChange={setSelectedCategories}
                    visibilityFilter={visibilityFilter}
                    onVisibilityChange={setVisibilityFilter}
                    accentColor={userProfile.qrCodeColor}
                  />
                  <Menu shadow="md" width={240} position="bottom-end" floatingStrategy="fixed">
                    <Menu.Target>
                      <MarketplaceToolbarButton
                        accentColor={userProfile.qrCodeColor}
                        active={sortOption !== 'none'}
                        leftIcon={<IconArrowsSort size={16} stroke={1.75} />}
                        rightIcon={<IconSelector size={15} stroke={1.75} />}
                      >
                        {t('marketplace.search.sortBy')}
                      </MarketplaceToolbarButton>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Label>{t('marketplace.search.sortBy')}</Menu.Label>
                      <Menu.Item
                        leftSection={<IconArrowsSort size={16} />}
                        rightSection={sortOption === 'none' ? <IconCheck size={14} /> : null}
                        onClick={() => setSortOption('none')}
                        style={
                          sortOption === 'none'
                            ? {
                                backgroundColor: `${userProfile.qrCodeColor}15`,
                                color: userProfile.qrCodeColor,
                                fontWeight: 600,
                              }
                            : {}
                        }
                      >
                        {t('marketplace.search.newest')}
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconSortAscending size={16} />}
                        rightSection={sortOption === 'date-asc' ? <IconCheck size={14} /> : null}
                        onClick={() => setSortOption('date-asc')}
                        style={
                          sortOption === 'date-asc'
                            ? {
                                backgroundColor: `${userProfile.qrCodeColor}15`,
                                color: userProfile.qrCodeColor,
                                fontWeight: 600,
                              }
                            : {}
                        }
                      >
                        {t('marketplace.search.oldest')}
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconSortDescending size={16} />}
                        rightSection={sortOption === 'views-desc' ? <IconCheck size={14} /> : null}
                        onClick={() => setSortOption('views-desc')}
                        style={
                          sortOption === 'views-desc'
                            ? {
                                backgroundColor: `${userProfile.qrCodeColor}15`,
                                color: userProfile.qrCodeColor,
                                fontWeight: 600,
                              }
                            : {}
                        }
                      >
                        {t('marketplace.search.popular')}
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconSortAscending size={16} />}
                        rightSection={sortOption === 'views-asc' ? <IconCheck size={14} /> : null}
                        onClick={() => setSortOption('views-asc')}
                        style={
                          sortOption === 'views-asc'
                            ? {
                                backgroundColor: `${userProfile.qrCodeColor}15`,
                                color: userProfile.qrCodeColor,
                                fontWeight: 600,
                              }
                            : {}
                        }
                      >
                        {t('marketplace.search.leastPopular')}
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Group>
              </Group>

              <ShowLinks
                products={productsToShow}
                productSlots={userPermissions.productSlots}
                deleteProduct={deleteProduct}
                corporateColor={userProfile.qrCodeColor}
                isLayout={false}
                onProductsRefresh={() => void getProducts()}
                onStarChange={patchProductStar}
              />
            </DashboardCollapsibleSection>

            <Paper withBorder p="md" radius="md" mt={28} id="trash">
              <PageHeader
                variant="section"
                title={t('dashboard.trash.title')}
                subtitle={t('dashboard.trash.hint')}
                accentColor={userProfile.qrCodeColor}
                mb="md"
              />
              {deletedProducts.length === 0 ? (
                <Text size="sm" c="dimmed">
                  {t('dashboard.trash.empty')}
                </Text>
              ) : (
                <Stack gap="sm">
                  {deletedProducts.map((p) => {
                    const daysLeft = daysRemainingInTrash(p.deletedAt);
                    return (
                      <Group key={p._id} justify="space-between" wrap="wrap" align="center">
                        <Group gap="sm" wrap="nowrap" align="flex-start" style={{ minWidth: 0, flex: 1 }}>
                          <Box
                            style={{
                              width: 52,
                              height: 52,
                              flexShrink: 0,
                              borderRadius: 8,
                              overflow: 'hidden',
                              backgroundColor: 'var(--mantine-color-default)',
                              border: '1px solid var(--mantine-color-default-border)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {p.productImage && p._id ? (
                              <ProductListPreviewPicture
                                entityId={p._id}
                                productImage={p.productImage}
                                imageVersion={p.imageVersion}
                                alt=""
                                fit="cover"
                              />
                            ) : (
                              <IconPhoto size={22} stroke={1.25} color="var(--mantine-color-gray-5)" />
                            )}
                          </Box>
                          <div style={{ minWidth: 0 }}>
                            <Text fw={500} lineClamp={2}>
                              {p.productName}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {t('dashboard.trash.daysRemaining', { count: daysLeft })}
                            </Text>
                          </div>
                        </Group>
                        <Group gap="xs">
                          <Button
                            size="xs"
                            variant="light"
                            color={userProfile.qrCodeColor}
                            leftSection={<IconRotateClockwise size={14} />}
                            onClick={() => handleRestoreDeletedProduct(p._id)}
                          >
                            {t('dashboard.trash.restore')}
                          </Button>
                          <Button
                            size="xs"
                            variant="light"
                            color="red"
                            leftSection={<IconTrash size={14} />}
                            onClick={() => handlePermanentDeleteFromTrash(p._id)}
                          >
                            {t('dashboard.trash.deleteForever')}
                          </Button>
                        </Group>
                      </Group>
                    );
                  })}
                </Stack>
              )}
            </Paper>

            <DashboardCollapsibleSection
              title={t('dashboard.counts.layoutsCount', {
                count: layouts.length || 0,
                total: userPermissions.layoutSlots || 0,
              })}
              accentColor={userProfile.qrCodeColor}
              expandLabel={t('dashboard.sections.expandLayouts')}
              collapseLabel={t('dashboard.sections.collapseLayouts')}
              mb={15}
              mt={30}
            >
              <Group mb={10} align="flex-start" justify="space-between">
                <TextInput
                  placeholder={t('dashboard.search.layouts')}
                  value={layoutSearchValue}
                  onChange={(e) => setLayoutSearchValue(e.currentTarget.value)}
                  leftSection={<IconSearch size={16} />}
                  size="sm"
                  style={{ width: '100%' }}
                />
              </Group>

              <ShowLinks
                products={filteredLayouts || layouts}
                productSlots={userPermissions.layoutSlots}
                deleteProduct={deleteLayout}
                corporateColor={userProfile.qrCodeColor}
                isLayout={true}
              />
            </DashboardCollapsibleSection>

            <br />
            {(userPermissions.productSlots === 0 ||
              products.length === userPermissions.productSlots) && (
              <button
                style={{ ...rowStyle, justifyContent: 'center' }}
                type="button"
                onClick={() => {
                  window.location.href =
                    'mailto:service@robotop.de' +
                    '?' +
                    `subject=Early bird Nutzeranfrage ${userProfile.email} von www.portfolio.robotop.de` +
                    '&body=Hallo! Wie können wir Dir weiterhelfen?' +
                    '&cc=tony.phan@robotop.de';
                }}
              >
                {t('dashboard.actions.requestSlot')}
              </button>
            )}
          </Box>
        </Box>
      </Center>
    </SessionAuth>
  );
}
